<!-- Presentational shape visual. Renders one vector shape given already-
     resolved style. Used by DesignCanvas for top-level shapes, the
     children of a component-instance group, and library symbols.
     `symbol` (D3) = an SVG made of one or more <path>s (industrial mimics). -->
<template>
  <svg v-if="kind === 'symbol'" class="svg" :viewBox="viewBox || '0 0 100 100'" preserveAspectRatio="xMidYMid meet">
    <path
      v-for="(p, i) in (paths ?? [])"
      :key="i"
      :d="p.d"
      :fill="p.fill === 'currentFill' ? (fill || '#9ca3af') : (p.fill ?? 'none')"
      :stroke="p.stroke === 'currentStroke' ? (stroke || '#374151') : (p.stroke ?? 'none')"
      :stroke-width="p.strokeWidth ?? 2"
      stroke-linejoin="round"
      stroke-linecap="round"
    />
  </svg>
  <svg v-else-if="kind === 'ellipse'" class="svg" viewBox="0 0 100 100" preserveAspectRatio="none">
    <ellipse cx="50" cy="50" rx="49" ry="49" :fill="fill" :stroke="stroke" :stroke-width="strokeWidth ?? 2" :stroke-dasharray="dashArray" />
  </svg>
  <svg v-else-if="kind === 'line'" class="svg" viewBox="0 0 100 100" preserveAspectRatio="none">
    <line x1="0" y1="50" x2="100" y2="50" :stroke="stroke || '#374151'" :stroke-width="strokeWidth ?? 3" :stroke-dasharray="dashArray" />
  </svg>
  <svg v-else-if="kind === 'path'" class="svg" viewBox="0 0 1000 1000" preserveAspectRatio="none">
    <path :d="pathD" :fill="closed ? (fill || '#cbd5e1') : 'none'" :stroke="stroke || '#374151'" :stroke-width="strokeWidth ?? 2" :stroke-dasharray="dashArray" vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round" />
  </svg>
  <div v-else-if="kind === 'text'" ref="txtBox" class="s-txt" :style="txtStyle"><span ref="txtSpan" :style="txtSpanStyle">{{ text }}</span></div>
  <img v-else-if="kind === 'image'" class="img" :src="src || PLACEHOLDER_IMG" draggable="false" :style="{ borderRadius: borderRadiusCss(radius), objectFit: (objectFit || undefined) as 'contain' | 'cover' | 'fill' | 'none' | 'scale-down' | undefined }" />
  <div v-else-if="kind === 'value'" class="val" :style="{ color: fill || 'var(--color-ink)' }">
    <span class="vnum" :style="{ fontSize: (fontSize || 28) + 'px' }">{{ text }}</span><span v-if="unit" class="vunit">{{ unit }}</span>
  </div>
  <div v-else-if="kind === 'button'" class="btn" :style="btnStyle">{{ text }}</div>
  <div v-else class="rect" :style="rectStyle"></div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { buildPathD } from './pathGeom';
export interface SymbolPath { d: string; fill?: string; stroke?: string; strokeWidth?: number }
export interface Gradient { type?: 'linear' | 'radial'; angle?: number; from: string; to: string }
export interface TextStyle { fontFamily?: string; fontWeight?: number | string; italic?: boolean; underline?: boolean; align?: 'left' | 'center' | 'right'; valign?: 'top' | 'middle' | 'bottom'; lineHeight?: number; letterSpacing?: number; writingMode?: string; ellipsis?: boolean }
const props = defineProps<{ kind: string; fill?: string; stroke?: string; text?: string; fontSize?: number; paths?: SymbolPath[]; viewBox?: string; radius?: number | number[] | { tl?: number; tr?: number; br?: number; bl?: number }; gradient?: Gradient | null; textStyle?: TextStyle; strokeWidth?: number; src?: string; unit?: string; points?: Array<{ x: number; y: number }>; closed?: boolean; curve?: string; textColor?: string; strokeStyle?: string; borderSides?: { t?: number; r?: number; b?: number; l?: number }; objectFit?: string; autoSize?: boolean }>();
// Dash pattern for SVG strokes (ellipse/line/path) from the border style.
const dashArray = computed(() => (props.strokeStyle === 'dashed' ? '6,4' : props.strokeStyle === 'dotted' ? '2,3' : undefined));
// border-radius: a number (uniform) OR per-corner [tl,tr,br,bl] / {tl,tr,br,bl}.
function borderRadiusCss(r: typeof props.radius): string | undefined {
  if (r == null) return undefined;
  if (Array.isArray(r)) return r.map((x) => `${Number(x) || 0}px`).join(' ');
  if (typeof r === 'object') return [r.tl, r.tr, r.br, r.bl].map((x) => `${Number(x) || 0}px`).join(' ');
  return `${Number(r) || 0}px`;
}
const pathD = computed(() => buildPathD(props.points ?? [], props.closed === true, props.curve === 'smooth'));
const PLACEHOLDER_IMG = 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="100" height="80"><rect width="100" height="80" fill="#e5e7eb"/><path d="M20 60 L40 35 L55 50 L70 30 L85 55 L85 70 L15 70 Z" fill="#9ca3af"/><circle cx="72" cy="22" r="7" fill="#9ca3af"/></svg>');

// Auto-size (fit text to box): when props.autoSize, binary-search the largest
// font that fits the element, recomputed on resize / text / style change.
const txtBox = ref<HTMLElement | null>(null);
const txtSpan = ref<HTMLElement | null>(null);
const fittedSize = ref<number | null>(null);

const txtStyle = computed(() => {
  const t = props.textStyle ?? {};
  const valign = t.valign ?? 'top';
  return {
    color: props.fill || 'var(--color-ink)',
    fontSize: ((props.autoSize && fittedSize.value != null) ? fittedSize.value : (props.fontSize || 14)) + 'px',
    fontFamily: t.fontFamily || 'system-ui, sans-serif',
    fontWeight: t.fontWeight ?? 400,
    fontStyle: t.italic ? 'italic' : 'normal',
    textDecoration: t.underline ? 'underline' : 'none',
    textAlign: t.align ?? 'left',
    lineHeight: t.lineHeight ? String(t.lineHeight) : '1.2',
    letterSpacing: t.letterSpacing != null ? t.letterSpacing + 'px' : undefined,
    // valign → align-items on a ROW flex (reliable vertical centering, no
    // line-height hack). justify-content centers the run horizontally for
    // vertical text (its span is content-width); horizontal text uses the
    // full-width span + text-align instead.
    flexDirection: 'row' as const,
    alignItems: valign === 'middle' ? 'center' : valign === 'bottom' ? 'flex-end' : 'flex-start',
    justifyContent: t.writingMode ? 'center' : undefined,
  };
});
const txtSpanStyle = computed(() => {
  const wm = props.textStyle?.writingMode;
  return {
    width: wm ? 'auto' : '100%', // vertical run = content-width so it can be centered
    writingMode: (wm || undefined) as 'horizontal-tb' | 'vertical-rl' | 'vertical-lr' | undefined,
    textAlign: (props.textStyle?.align ?? 'left') as 'left' | 'center' | 'right',
    ...(props.textStyle?.ellipsis ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
  };
});

// Largest font (≤ box height) at which the text still fits the element box.
function fitText(): void {
  if (props.kind !== 'text' || !props.autoSize) { fittedSize.value = null; return; }
  const box = txtBox.value, span = txtSpan.value;
  if (!box || !span) return;
  const maxW = box.clientWidth, maxH = box.clientHeight;
  if (maxW < 2 || maxH < 2 || !(props.text ?? '').length) return;
  // Measure overflow on the BOX (.txt has overflow:hidden, so its scrollW/H
  // report the full content size even when it spills) — the span sits in a flex
  // column and its own scroll size doesn't reflect overflow reliably.
  const fits = (size: number): boolean => {
    span.style.fontSize = size + 'px';
    return box.scrollWidth <= box.clientWidth + 0.5 && box.scrollHeight <= box.clientHeight + 0.5;
  };
  let lo = 4, hi = Math.max(6, Math.ceil(maxH)), best = lo;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (fits(mid)) { best = mid; lo = mid + 1; } else { hi = mid - 1; }
  }
  span.style.fontSize = ''; // drop the probe; txtStyle's fontSize (fittedSize) applies
  fittedSize.value = best;
}
function scheduleFit(): void { void nextTick(() => fitText()); }
let ro: ResizeObserver | null = null;
onMounted(() => {
  if (typeof ResizeObserver !== 'undefined' && txtBox.value) { ro = new ResizeObserver(() => fitText()); ro.observe(txtBox.value); }
  scheduleFit();
});
onBeforeUnmount(() => ro?.disconnect());
watch(() => [props.autoSize, props.text, props.fontSize, props.textStyle, props.kind], scheduleFit, { deep: true });

// Button = a filled box (fill/gradient/border/radius like a rect) with a
// centered label coloured by textColor.
const btnStyle = computed(() => ({
  ...rectStyle.value,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: props.textColor || '#ffffff',
  fontSize: (props.fontSize || 14) + 'px',
  fontWeight: 600,
  width: '100%', height: '100%', overflow: 'hidden', cursor: 'inherit',
}));

const rectStyle = computed(() => {
  const g = props.gradient;
  const bg = g ? (g.type === 'radial' ? `radial-gradient(circle, ${g.from}, ${g.to})` : `linear-gradient(${g.angle ?? 180}deg, ${g.from}, ${g.to})`) : (props.fill || '#9ca3af');
  const col = props.stroke || '#6b7280', st = props.strokeStyle || 'solid';
  const base: Record<string, string | undefined> = { background: bg, borderRadius: borderRadiusCss(props.radius) };
  const s = props.borderSides;
  if (s) return { ...base, borderStyle: st, borderColor: col, borderTopWidth: (s.t ?? 0) + 'px', borderRightWidth: (s.r ?? 0) + 'px', borderBottomWidth: (s.b ?? 0) + 'px', borderLeftWidth: (s.l ?? 0) + 'px' };
  return { ...base, border: `${props.strokeWidth ?? 1}px ${st} ${col}` };
});
</script>

<style scoped>
.svg { width: 100%; height: 100%; display: block; }
.rect { width: 100%; height: 100%; border-radius: 3px; }
/* Unique class (was `.txt`, which collided with DesignCanvas's own scoped `.txt`
   rule leaking onto this child-root element and forcing display:inline-block). */
.s-txt { width: 100%; height: 100%; display: flex; flex-direction: column; white-space: pre-wrap; overflow: hidden; word-break: break-word; }
.img { width: 100%; height: 100%; object-fit: contain; display: block; -webkit-user-drag: none; user-select: none; }
.val { width: 100%; height: 100%; display: flex; align-items: baseline; justify-content: center; gap: 4px; overflow: hidden; }
.btn { white-space: nowrap; box-sizing: border-box; }
.vnum { font-weight: 700; line-height: 1; }
.vunit { font-size: .7em; opacity: .7; }
</style>
